import React, { useEffect, useState } from 'react';
import { ref, get, push, set, update } from 'firebase/database';
import { db } from '../firebase/config';
import { useNavigate } from 'react-router-dom';

function isActiveTraining(training) {
  if (training.status === 'active') return true;
  if (training.expireDate) {
    const now = Date.now();
    const exp = new Date(training.expireDate).getTime();
    if (exp > now) return true;
  }
  return false;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');


  const SMS_CONFIG = {
    username: "Experts",
    authkey: "ba9dcdcdfcXX",
    senderId: "EXTSKL",
    accusage: "1"
  };

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [trainingsPerPage] = useState(10);

  // Create Training Form States
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [form, setForm] = useState({
    location: '',
    venue: '',
    startDate: '',
    endDate: '',
    time: '',
    products: '',
    candidates: '',
    trainerId: '',
    coTrainerId: '',
    expireDate: '',
    expireTime: '23:59',
    accessDuration: '',
    fees: '',
    photo: '',
  });

  // Copy link state
  const [copiedId, setCopiedId] = useState(null);

  // Image file state
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Navigation function for trainers page
  const handleNavigateToTrainers = () => {
    navigate('/company-dashboard/trainers');
  };

  // Handle image file selection
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Please select a valid image file');
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert('Image size should be less than 5MB');
        return;
      }

      setSelectedImage(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);

      // Update form with file name
      setForm(prev => ({ ...prev, photo: file.name }));
    }
  };

  useEffect(() => {
    async function fetchAll() {
      setLoading(true);
      try {
        const [tSnap, uSnap, trainersSnap] = await Promise.all([
          get(ref(db, 'HTAMS/company/trainings')),
          get(ref(db, 'HTAMS/users')),
          get(ref(db, 'HTAMS/company/trainers')),
        ]);

        const tArr = tSnap.exists()
          ? Object.entries(tSnap.val()).map(([id, t]) => {
            console.log(`Training ${id}:`, {
              date: t.date,
              expireDate: t.expireDate,
              startDate: t.startDate,
              trainerName: t.trainerName
            });
            return { id, ...t };
          })
          : [];

        const uArr = uSnap.exists()
          ? Object.entries(uSnap.val()).map(([uid, u]) => ({ ...u, uid }))
          : [];

        const trainersArr = trainersSnap.exists()
          ? Object.entries(trainersSnap.val()).map(([id, trainer]) => ({ id, ...trainer }))
          : [];

        // Sort trainings by date (latest first)
        const sortedTrainings = tArr.sort((a, b) => {
          const dateA = new Date(getStartDate(a) || '1900-01-01').getTime();
          const dateB = new Date(getStartDate(b) || '1900-01-01').getTime();
          return dateB - dateA; // Descending order (latest first)
        });

        setTrainings(sortedTrainings);
        setUsers(uArr);
        setTrainers(trainersArr);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
  }, []);

  // Create Training Form Functions
  const toggleCreateForm = () => {
    setShowCreateForm(prev => !prev);
    if (showCreateForm) {
      resetForm();
      setEditingTraining(null);
    }
  };

  const resetForm = () => {
    setForm({
      location: '',
      venue: '',
      startDate: '',
      endDate: '',
      time: '',
      products: '',
      candidates: '',
      trainerId: '',
      coTrainerId: '',
      expireDate: '',
      expireTime: '23:59',
      accessDuration: '',
      fees: '',
      photo: '',
    });
    setEditingTraining(null);
    setSelectedImage(null);
    setImagePreview(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // Handle DD/MM/YYYY date input
  const handleDateInput = (e, fieldName) => {
    let value = e.target.value.replace(/[^0-9]/g, ''); // Only numbers

    // Format as DD/MM/YYYY
    if (value.length >= 2) {
      value = value.slice(0, 2) + '/' + value.slice(2);
    }
    if (value.length >= 5) {
      value = value.slice(0, 5) + '/' + value.slice(5, 9);
    }

    if (value.length <= 10) {
      setForm(prev => ({ ...prev, [fieldName]: value }));
    }
  };

  // Validate DD/MM/YYYY date format
  const validateDate = (dateString) => {
    if (!dateString || dateString.length !== 10) return false;

    const [day, month, year] = dateString.split('/');
    if (!day || !month || !year) return false;

    const dayNum = parseInt(day);
    const monthNum = parseInt(month);
    const yearNum = parseInt(year);

    if (dayNum < 1 || dayNum > 31) return false;
    if (monthNum < 1 || monthNum > 12) return false;
    if (yearNum < 1900 || yearNum > 2100) return false;

    // Check if date is valid
    const date = new Date(yearNum, monthNum - 1, dayNum);
    return date.getDate() === dayNum &&
      date.getMonth() === monthNum - 1 &&
      date.getFullYear() === yearNum;
  };

  // Convert DD/MM/YYYY to Date object for comparison
  const parseDate = (dateString) => {
    if (!dateString || dateString.length !== 10) return null;
    const [day, month, year] = dateString.split('/');
    return new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  };

  // Convert ISO date (YYYY-MM-DD) to DD/MM/YYYY for editing
  const convertISOtoDDMMYYYY = (isoDate) => {
    if (!isoDate) return '';
    const [year, month, day] = isoDate.split('-');
    if (!year || !month || !day) return '';
    return `${day}/${month}/${year}`;
  };

  // Edit training function
  const handleEditTraining = (training) => {
    setEditingTraining(training);
    setForm({
      location: training.location || '',
      venue: training.venue || '',
      startDate: convertISOtoDDMMYYYY(training.startDate) || '',
      endDate: convertISOtoDDMMYYYY(training.endDate) || '',
      time: training.time || '',
      products: Array.isArray(training.products)
        ? training.products.join(', ')
        : training.products || '',
      candidates: training.candidates ? training.candidates.toString() : '',
      trainerId: training.trainerId || '',
      coTrainerId: training.coTrainerId || '',
      expireDate: convertISOtoDDMMYYYY(training.expireDate) || '',
      expireTime: training.expireTime || '23:59',
      accessDuration: training.accessDuration ? training.accessDuration.toString() : '',
      fees: training.fees !== undefined ? training.fees.toString() : '',
      photo: training.photo || '',
    });
    setSelectedImage(null);
    setImagePreview(null);
    setShowCreateForm(true);
  };

  // Copy link function
  const copyTrainingLink = async (link, trainingId) => {
    try {
      await navigator.clipboard.writeText(link);
      setCopiedId(trainingId);
      setTimeout(() => setCopiedId(null), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopiedId(trainingId);
      setTimeout(() => setCopiedId(null), 2000);
    }
  };

  const validateDates = () => {
    // Validate date formats first
    if (!validateDate(form.startDate)) {
      return { valid: false, message: 'Please enter a valid start date in DD/MM/YYYY format' };
    }
    if (!validateDate(form.endDate)) {
      return { valid: false, message: 'Please enter a valid end date in DD/MM/YYYY format' };
    }
    if (!validateDate(form.expireDate)) {
      return { valid: false, message: 'Please enter a valid expire date in DD/MM/YYYY format' };
    }

    // Parse DD/MM/YYYY dates
    const startDate = parseDate(form.startDate);
    const endDate = parseDate(form.endDate);
    const expireDate = parseDate(form.expireDate);
    const today = new Date();

    today.setHours(0, 0, 0, 0);
    startDate.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    expireDate.setHours(0, 0, 0, 0);

    if (startDate < today) {
      return { valid: false, message: 'Training start date cannot be before today' };
    }

    if (endDate < startDate) {
      return { valid: false, message: 'Training end date cannot be before start date' };
    }

    if (expireDate < today) {
      return { valid: false, message: 'Link expiration date cannot be before today' };
    }

    if (expireDate >= startDate) {
      return { valid: false, message: 'Link expiration date must be before training start date' };
    }

    return { valid: true };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ADD THIS FIX - Prevent double submission
    if (e.target.disabled) return;
    e.target.disabled = true;

    try {
      const selectedTrainer = trainers.find((t) => t.id === form.trainerId);
      if (!selectedTrainer) {
        alert('Please select a trainer');
        return;
      }

      const dateValidation = validateDates();
      if (!dateValidation.valid) {
        alert(dateValidation.message);
        return;
      }

      if (isNaN(parseInt(form.candidates)) || parseInt(form.candidates) <= 0) {
        alert('Candidate count must be a valid positive number');
        return;
      }
      if (isNaN(parseInt(form.accessDuration)) || parseInt(form.accessDuration) <= 0) {
        alert('Access duration must be a valid positive number');
        return;
      }
      if (form.fees === '' || isNaN(parseFloat(form.fees)) || parseFloat(form.fees) < 0) {
        alert('Fees must be a valid positive number');
        return;
      }

      // Get co-trainer information
      const selectedCoTrainer = form.coTrainerId ? trainers.find((t) => t.id === form.coTrainerId) : null;

      // Convert DD/MM/YYYY dates to ISO format (YYYY-MM-DD) for database storage
      const convertDDMMYYYYtoISO = (ddmmyyyy) => {
        if (!ddmmyyyy) return '';
        const [day, month, year] = ddmmyyyy.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      };

      const dataToSave = {
        ...form,
        startDate: convertDDMMYYYYtoISO(form.startDate),
        endDate: convertDDMMYYYYtoISO(form.endDate),
        expireDate: convertDDMMYYYYtoISO(form.expireDate),
        products: form.products.split(',').map((p) => p.trim()).filter(p => p.length > 0),
        candidates: parseInt(form.candidates),
        fees: parseFloat(form.fees),
        accessDuration: parseInt(form.accessDuration),
        trainerName: selectedTrainer.name || '',
        trainerPhone: selectedTrainer.phone || selectedTrainer.mobile || selectedTrainer.contact || '',
        coTrainerName: selectedCoTrainer ? selectedCoTrainer.name : '',
        coTrainerPhone: selectedCoTrainer ? (selectedCoTrainer.phone || selectedCoTrainer.mobile || selectedCoTrainer.contact || '') : '',
        photo: form.photo || '',
        status: form.status || 'pending',
      };

      if (editingTraining) {
        // UPDATE EXISTING TRAINING
        const trainingRef = ref(db, `HTAMS/company/trainings/${editingTraining.id}`);
        await update(trainingRef, dataToSave);

        const updatedTrainings = trainings.map(training =>
          training.id === editingTraining.id
            ? { ...training, ...dataToSave }
            : training
        ).sort((a, b) => {
          const dateA = new Date(getStartDate(a) || '1900-01-01').getTime();
          const dateB = new Date(getStartDate(b) || '1900-01-01').getTime();
          return dateB - dateA;
        });

        setTrainings(updatedTrainings);

        // Send SMS notifications to participants
        if (editingTraining.participants) {
          const participantMobiles = extractParticipantMobiles(editingTraining.participants);

          if (participantMobiles.length > 0) {
            console.log('Sending SMS to participants:', participantMobiles);

            const smsResult = await sendTrainingUpdateMessage(
              { ...editingTraining, ...dataToSave },
              participantMobiles
            );

            console.log('SMS Result:', smsResult);

            if (smsResult.success) {
              alert(`Training updated successfully! ${smsResult.message}`);
            } else {
              alert(`Training updated successfully, but some SMS failed: ${smsResult.message}`);
            }
          } else {
            alert('Training updated successfully! (No valid participant mobile numbers)');
          }
        } else {
          alert('Training updated successfully! (No participants found)');
        }

      } else {
        // CREATE NEW TRAINING
        const newRef = push(ref(db, 'HTAMS/company/trainings'));
        const trainingId = newRef.key;
        const joinLink = `${window.location.origin}/join-training/${trainingId}`;

        const newTrainingData = {
          ...dataToSave,
          id: trainingId,
          status: 'pending',
          joinLink,
          joinedCount: 0
        };

        const updatedTrainings = [newTrainingData, ...trainings].sort((a, b) => {
          const dateA = new Date(getStartDate(a) || '1900-01-01').getTime();
          const dateB = new Date(getStartDate(b) || '1900-01-01').getTime();
          return dateB - dateA;
        });

        setTrainings(updatedTrainings);
        await set(newRef, newTrainingData);
        alert('Training created successfully');
      }

      resetForm();
      setShowCreateForm(false);

    } catch (error) {
      alert('Failed to save training: ' + error.message);
    } finally {
      // Re-enable the form
      e.target.disabled = false;
    }
  };



  // Stats calculation
  const participantCount = trainings.reduce(
    (sum, t) => sum + (t.participants ? Object.keys(t.participants).length : 0), 0);
  const activeCount = trainings.filter(isActiveTraining).length;
  const completedCount = trainings.filter(t => t.status === 'completed').length;

  // ENHANCED SEARCH AND FILTER FUNCTIONALITY
  const filteredTrainings = trainings.filter(t => {
    // Enhanced search functionality - searches in multiple fields
    const matchesSearch = !searchTerm ||
      t.trainerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      t.venue?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (Array.isArray(t.products) && t.products.some(product =>
        product.toLowerCase().includes(searchTerm.toLowerCase())
      )) ||
      (typeof t.products === 'string' && t.products.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = filterStatus === 'all' ||
      (filterStatus === 'active' && isActiveTraining(t)) ||
      (filterStatus === 'pending' && t.status === 'pending') ||
      (filterStatus === 'completed' && t.status === 'completed');

    return matchesSearch && matchesStatus;
  });

  // Pagination calculations
  const totalTrainings = filteredTrainings.length;
  const totalPages = Math.ceil(totalTrainings / trainingsPerPage);
  const indexOfLastTraining = currentPage * trainingsPerPage;
  const indexOfFirstTraining = indexOfLastTraining - trainingsPerPage;
  const currentTrainings = filteredTrainings.slice(indexOfFirstTraining, indexOfLastTraining);

  // Clear search function
  const clearSearch = () => {
    setSearchTerm('');
    setFilterStatus('all');
  };

  // Enhanced formatDate function
  const formatDate = (dateString, fieldName = 'unknown') => {
    if (!dateString || dateString === '' || dateString === null || dateString === undefined) {
      return '—';
    }

    try {
      const date = new Date(dateString);

      if (isNaN(date.getTime())) {
        return '—';
      }

      // Format as DD/MM/YYYY
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    } catch (error) {
      return '—';
    }
  };

  // Enhanced expire date information function
  const getExpireInfo = (training) => {
    if (!training.expireDate) {
      return {
        status: 'No Expiry',
        statusColor: '#6b7280',
        bgColor: '#f3f4f6',
        icon: '♾️',
        daysLeft: null,
        urgency: 'none',
        message: 'Link never expires',
        detailedMessage: 'This training link does not have an expiration date',
        exactDateTime: null
      };
    }

    const now = new Date();
    const expireDateTime = new Date(training.expireDate);

    // Set time if available, otherwise use end of day (23:59)
    if (training.expireTime) {
      const [hours, minutes] = training.expireTime.split(':');
      expireDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    } else {
      expireDateTime.setHours(23, 59, 59, 999);
    }

    // Check if manually expired
    const isManuallyExpired = training.expiresAt && Date.now() > training.expiresAt;

    // Calculate time difference
    const diffTime = expireDateTime - now;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    const diffHours = Math.floor((diffTime % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const diffMinutes = Math.floor((diffTime % (1000 * 60 * 60)) / (1000 * 60));

    // Format exact expiration time
    const formatExactDateTime = (date) => {
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      const period = date.getHours() >= 12 ? 'PM' : 'AM';
      const displayHours = date.getHours() > 12 ? (date.getHours() - 12).toString().padStart(2, '0') :
        date.getHours() === 0 ? '12' : hours;

      return `${day}/${month}/${year}, ${displayHours}:${minutes} ${period}`;
    };

    // Determine status based on conditions
    if (isManuallyExpired) {
      return {
        status: 'Manually Expired',
        statusColor: '#dc2626',
        bgColor: '#fef2f2',
        icon: '🚫',
        daysLeft: null,
        urgency: 'critical',
        message: 'Link was manually expired',
        detailedMessage: `Manually expired on ${formatDate(training.expiresAt)}`,
        exactDateTime: formatExactDateTime(new Date(training.expiresAt))
      };
    }

    if (diffTime < 0) {
      const pastDays = Math.abs(diffDays);
      const pastHours = Math.abs(diffHours);
      const pastMinutes = Math.abs(diffMinutes);

      let timeAgo = '';
      if (pastDays > 0) {
        timeAgo = `${pastDays} day${pastDays !== 1 ? 's' : ''} ago`;
      } else if (pastHours > 0) {
        timeAgo = `${pastHours} hour${pastHours !== 1 ? 's' : ''} ago`;
      } else {
        timeAgo = `${pastMinutes} minute${pastMinutes !== 1 ? 's' : ''} ago`;
      }

      return {
        status: 'Expired',
        statusColor: '#dc2626',
        bgColor: '#fef2f2',
        icon: '⏰',
        daysLeft: Math.abs(diffDays),
        urgency: 'critical',
        message: `Expired ${timeAgo}`,
        detailedMessage: `Expired on ${formatExactDateTime(expireDateTime)} (${timeAgo})`,
        exactDateTime: formatExactDateTime(expireDateTime)
      };
    }

    if (diffDays === 0 && diffHours === 0 && diffMinutes <= 30) {
      return {
        status: 'Expires Soon',
        statusColor: '#ef4444',
        bgColor: '#fef2f2',
        icon: '🚨',
        daysLeft: 0,
        urgency: 'critical',
        message: `Expires in ${diffMinutes} minute${diffMinutes !== 1 ? 's' : ''}!`,
        detailedMessage: `Expires at ${formatExactDateTime(expireDateTime)} (${diffMinutes} minutes remaining)`,
        exactDateTime: formatExactDateTime(expireDateTime)
      };
    }

    if (diffDays === 0) {
      if (diffHours === 0) {
        return {
          status: 'Expires Today',
          statusColor: '#f59e0b',
          bgColor: '#fef3c7',
          icon: '⚠️',
          daysLeft: 0,
          urgency: 'high',
          message: `Expires in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`,
          detailedMessage: `Expires today at ${expireDateTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`,
          exactDateTime: formatExactDateTime(expireDateTime)
        };
      } else {
        return {
          status: 'Expires Today',
          statusColor: '#f59e0b',
          bgColor: '#fef3c7',
          icon: '⚠️',
          daysLeft: 0,
          urgency: 'high',
          message: `Expires in ${diffHours} hour${diffHours !== 1 ? 's' : ''}`,
          detailedMessage: `Expires today at ${expireDateTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`,
          exactDateTime: formatExactDateTime(expireDateTime)
        };
      }
    }

    if (diffDays === 1) {
      return {
        status: 'Expires Tomorrow',
        statusColor: '#f59e0b',
        bgColor: '#fef3c7',
        icon: '⏳',
        daysLeft: 1,
        urgency: 'high',
        message: 'Expires tomorrow',
        detailedMessage: `Expires tomorrow at ${expireDateTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`,
        exactDateTime: formatExactDateTime(expireDateTime)
      };
    }

    if (diffDays <= 7) {
      return {
        status: `${diffDays} Days Left`,
        statusColor: '#d97706',
        bgColor: '#fed7aa',
        icon: '📅',
        daysLeft: diffDays,
        urgency: 'medium',
        message: `Expires in ${diffDays} day${diffDays !== 1 ? 's' : ''}`,
        detailedMessage: `Expires on ${formatDate(training.expireDate)} at ${expireDateTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`,
        exactDateTime: formatExactDateTime(expireDateTime)
      };
    }

    return {
      status: 'Active',
      statusColor: '#059669',
      bgColor: '#d1fae5',
      icon: '✅',
      daysLeft: diffDays,
      urgency: 'low',
      message: `Link valid for ${diffDays} more days`,
      detailedMessage: `Expires on ${formatDate(training.expireDate)} at ${expireDateTime.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true })}`,
      exactDateTime: formatExactDateTime(expireDateTime)
    };
  };

  // Function to get start date from multiple possible fields
  const getStartDate = (training) => {
    return training.startDate || training.date || training.trainingDate || null;
  };

  // Function to get expire date
  const getExpireDate = (training) => {
    return training.expireDate || training.endDate || training.expiryDate || null;
  };

  // Real-time expiration checker
  useEffect(() => {
    const checkExpiredLinks = async () => {
      const now = new Date();
      now.setHours(0, 0, 0, 0);

      const expiredTrainings = trainings.filter(training => {
        if (!training.expireDate) return false;
        const expireDate = new Date(training.expireDate);
        expireDate.setHours(0, 0, 0, 0);
        return now > expireDate && !training.expiresAt; // Only expire if not already manually expired
      });

      // Update expired trainings in database
      for (const training of expiredTrainings) {
        try {
          await update(ref(db, `HTAMS/company/trainings/${training.id}`), {
            expiresAt: Date.now(),
            status: 'expired'
          });
        } catch (error) {
          console.error('Error updating expired training:', error);
        }
      }

      // Update local state if there are expired trainings
      if (expiredTrainings.length > 0) {
        setTrainings(prev => prev.map(training => {
          const isExpired = expiredTrainings.some(t => t.id === training.id);
          return isExpired ? { ...training, expiresAt: Date.now(), status: 'expired' } : training;
        }));
      }
    };

    // Check immediately and then every hour
    checkExpiredLinks();
    const interval = setInterval(checkExpiredLinks, 60 * 60 * 1000); // Check every hour

    return () => clearInterval(interval);
  }, [trainings]);

  // Function to check if link is accessible
  const isLinkAccessible = (training) => {
    if (!training.expireDate) return false;

    const now = new Date();
    const expireDateTime = new Date(training.expireDate);

    // Set time if available, otherwise use end of day (23:59)
    if (training.expireTime) {
      const [hours, minutes] = training.expireTime.split(':');
      expireDateTime.setHours(parseInt(hours), parseInt(minutes), 0, 0);
    } else {
      expireDateTime.setHours(23, 59, 59, 999);
    }

    // Check both date-based expiration and manual expiration
    if (training.expiresAt && Date.now() > training.expiresAt) {
      return false;
    }

    return now <= expireDateTime; // Link is accessible if current time is less than or equal to expire date and time
  };

  // Pagination handlers
  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const sendTrainingUpdateMessage = async (training, participantMobiles) => {
    const results = [];

    for (const mobile of participantMobiles) {
      try {
        const updateCode = String(Math.floor(Math.random() * 900000) + 100000);
        const safeLocation = training.location.replace(/"/g, '');
        const message = `Training ${safeLocation} has been updated. Your Verification Code is ${updateCode}. - Expertskill Technology.`;
        const encodedMessage = encodeURIComponent(message);

        const accusage = "1";
        const username = "Experts";
        const authkey = "ba9dcdcdfcXX";
        const mobiles = "+91" + mobile.trim();
        const senderId = "EXTSKL";

        const mainUrl = "https://mobicomm.dove-sms.com/submitsms.jsp?";
        const url = `${mainUrl}user=${username}&key=${authkey}&mobile=${mobiles}&message=${encodedMessage}&accusage=${accusage}&senderid=${senderId}`;

        console.log(`Sending to ${mobile} → URL:`, url);

        const response = await fetch(url, { method: 'GET' });
        const responseText = await response.text();

        const isSuccess = response.ok && responseText.toLowerCase().includes("success");

        if (!isSuccess) {
          console.warn(`SMS failed for ${mobile}:`, responseText);
        } else {
          console.log(`SMS sent successfully to ${mobile}`);
        }

        results.push({
          mobile,
          success: isSuccess,
          response: responseText
        });

        await new Promise(resolve => setTimeout(resolve, 500));

      } catch (error) {
        console.error(`Error sending SMS to ${mobile}:`, error.message);
        results.push({ mobile, success: false, error: error.message });
      }
    }

    const successCount = results.filter(r => r.success).length;
    const totalCount = results.length;

    return {
      success: successCount > 0,
      message: `Training update notifications sent to ${successCount}/${totalCount} participants`,
      results
    };
  };

  const extractParticipantMobiles = (participants) => {
    if (!participants || typeof participants !== 'object') return [];

    return Object.values(participants)
      .map(participant => {
        return participant.mobile || participant.phone || participant.contact || null;
      })
      .filter(mobile => mobile && mobile.length >= 10)
      .map(mobile => {
        return mobile.replace(/[^\d]/g, '').slice(-10);
      });
  };

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterStatus]);

  // Professional White Theme Styles
  const styles = {
    // Root container - clean white background
    dashPremiumRoot: {
      minHeight: '100vh',
      background: '#f8fafc',
      fontFamily: "'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
      padding: 'clamp(15px, 3vw, 25px)',
      boxSizing: 'border-box',
    },

    // Header section - professional white design
    dashPremiumHeader: {
      background: 'var(--mobile-bg-primary)',
      border: '1px solid #e5e7eb',
      borderRadius: '12px',
      padding: '24px',
      marginBottom: '24px',
      boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15), 0 2px 8px rgba(99, 102, 241, 0.1)',
      width: '100%',
      boxSizing: 'border-box',
    },

    headerTitleSection: {
      textAlign: 'center',
      marginBottom: '20px',
    },

    dashPremiumPageTitle: {
      fontSize: 'clamp(1.5rem, 4vw, 2rem)',
      fontWeight: '700',
      color: 'var(--mobile-text-primary)',
      margin: 0,
      lineHeight: '1.2',
      wordBreak: 'break-word',
    },

    dashPremiumPageSubtitle: {
      fontSize: 'clamp(0.875rem, 3vw, 1rem)',
      color: 'var(--mobile-text-secondary)',
      margin: '8px 0 0 0',
      fontWeight: '400',
      lineHeight: '1.4',
      wordBreak: 'break-word',
    },

    // Controls section - clean white design
    controlsSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '16px',
      background: 'var(--mobile-bg-primary)',
      borderRadius: '8px',
      padding: '20px',
      // border: '1px solid #e5e7eb',
      width: '100%',
      boxSizing: 'border-box',
    },

    // Search section responsive design
    searchSection: {
      display: 'flex',
      flexDirection: 'column',
      gap: '15px',
      width: '100%',
    },

    searchFiltersRow: {
      display: 'flex',
      gap: '15px',
      width: '100%',
      flexWrap: 'wrap',
    },

    searchWrapper: {
      position: 'relative',
      flex: '1',
      minWidth: '200px',
      display: 'flex',
      alignItems: 'center',
    },

    searchIcon: {
      position: 'absolute',
      left: '16px',
      top: '50%',
      transform: 'translateY(-50%)',
      fontSize: '1.2rem',
      color: '#6366f1',
      zIndex: 2,
      pointerEvents: 'none',
    },

    searchInput: {
      width: '100%',
      padding: '12px 16px 12px 48px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
      background: 'white',
      color: '#374151',
      minHeight: '44px',
      fontWeight: '400',
      boxShadow: '0 2px 8px rgba(59, 130, 246, 0.15)',
    },

    clearSearchBtn: {
      position: 'absolute',
      right: '12px',
      top: '50%',
      transform: 'translateY(-50%)',
      background: 'transparent',
      border: 'none',
      color: '#6b7280',
      fontSize: '18px',
      cursor: 'pointer',
      padding: '4px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 2,
      transition: 'all 0.2s ease',
      width: '28px',
      height: '28px',
    },

    filterWrapper: {
      flex: '1',
      minWidth: '150px',
      display: 'flex',
      alignItems: 'center',
    },

    filterSelect: {
      width: '100%',
      padding: '12px 16px',
      border: '1px solid #d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      background: 'white',
      color: '#374151',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxSizing: 'border-box',
      minHeight: '44px',
      fontWeight: '400',
      boxShadow: '0 2px 8px rgba(16, 185, 129, 0.15)',
    },

    // Enhanced responsive buttons section
    buttonsSection: {
      display: 'flex',
      gap: '15px',
      justifyContent: 'center',
      width: '100%',
      flexWrap: 'wrap',
    },

    actionBtn: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '8px',
      padding: '12px 20px',
      border: '1px solid transparent',
      borderRadius: '8px',
      fontWeight: '600',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      minWidth: '120px',
      minHeight: '44px',
      whiteSpace: 'nowrap',
      position: 'relative',
      overflow: 'hidden',
      letterSpacing: '0.025em',
      flex: '1 1 auto',
      maxWidth: '200px',
    },

    actionBtnPrimary: {
      background: '#3b82f6',
      color: 'white',
      borderColor: '#3b82f6',
    },

    actionBtnDanger: {
      background: '#ef4444',
      color: 'white',
      borderColor: '#ef4444',
    },

    actionBtnSuccess: {
      background: '#10b981',
      color: 'white',
      borderColor: '#10b981',
    },

    btnIcon: {
      fontSize: 'clamp(16px, 3vw, 18px)',
      fontWeight: 'bold',
    },

    // Search results info - clean design
    searchResultsInfo: {
      background: 'white',
      borderRadius: '8px',
      padding: '16px 20px',
      marginBottom: '20px',
      color: '#374151',
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      flexWrap: 'wrap',
      gap: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
      boxSizing: 'border-box',
    },

    searchResultsText: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexWrap: 'wrap',
    },

    resultCount: {
      background: '#3b82f6',
      padding: '4px 12px',
      borderRadius: '16px',
      fontWeight: '600',
      color: 'white',
      fontSize: '12px',
    },

    clearAllBtn: {
      background: '#f3f4f6',
      border: '1px solid #d1d5db',
      color: '#374151',
      padding: '8px 16px',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '13px',
      fontWeight: '500',
      transition: 'all 0.2s ease',
      whiteSpace: 'nowrap',
    },

    // Form container - fully responsive
    createTrainingFormContainer: {
      background: 'white',
      borderRadius: 'clamp(15px, 3vw, 20px)',
      padding: 'clamp(20px, 4vw, 30px)',
      marginBottom: 'clamp(20px, 4vw, 30px)',
      boxShadow: '0 25px 50px rgba(168, 85, 247, 0.15), 0 10px 25px rgba(168, 85, 247, 0.1)',
      border: '1px solid rgba(255,255,255,0.2)',
      width: '100%',
      boxSizing: 'border-box',
    },

    formHeader: {
      marginBottom: '25px',
      textAlign: 'center',
    },

    formTitle: {
      fontSize: 'clamp(1.3rem, 4vw, 1.8rem)',
      fontWeight: '700',
      color: '#1f2937',
      margin: 0,
    },

    trainingForm: {
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      width: '100%',
    },

    formGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
      gap: '20px',
      width: '100%',
    },

    formGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px',
      width: '100%',
    },

    formLabel: {
      fontWeight: '600',
      color: '#374151',
      fontSize: 'clamp(13px, 3vw, 14px)',
    },

    formInput: {
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: 'clamp(8px, 2vw, 10px)',
      fontSize: 'clamp(13px, 3vw, 14px)',
      transition: 'all 0.2s ease',
      background: 'white',
      width: '100%',
      boxSizing: 'border-box',
      minHeight: '44px',
    },

    dateDisplay: {
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: '#f3f4f6',
      borderRadius: '6px',
      fontSize: '13px',
      color: '#374151',
      fontWeight: '500',
      border: '1px solid #e5e7eb',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },

    expireDateDisplay: {
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: '#fef3c7',
      borderRadius: '6px',
      fontSize: '13px',
      color: '#92400e',
      fontWeight: '500',
      border: '1px solid #fbbf24',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },

    // Date/Time input styles
    dateTimeContainer: {
      display: 'flex',
      gap: '10px',
      width: '100%',
    },

    dateInputWrapper: {
      flex: '2',
    },

    timeInputWrapper: {
      flex: '1',
    },

    dateInput: {
      minWidth: '150px',
    },

    timeInput: {
      minWidth: '120px',
    },

    expireHelper: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      marginTop: '8px',
      padding: '8px 12px',
      backgroundColor: '#f0f9ff',
      border: '1px solid #bfdbfe',
      borderRadius: '6px',
      fontSize: 'clamp(11px, 2.5vw, 12px)',
      color: '#1e40af',
    },

    expireHelperIcon: {
      fontSize: 'clamp(12px, 2.5vw, 14px)',
      flexShrink: 0,
      marginTop: '1px',
    },

    expireHelperText: {
      lineHeight: '1.4',
      fontWeight: '500',
    },

    formSelect: {
      padding: '12px 16px',
      border: '2px solid #e5e7eb',
      borderRadius: 'clamp(8px, 2vw, 10px)',
      fontSize: 'clamp(13px, 3vw, 14px)',
      transition: 'all 0.2s ease',
      background: 'white',
      width: '100%',
      boxSizing: 'border-box',
      minHeight: '44px',
    },

    formSubmitBtn: {
      background: 'linear-gradient(135deg, #059669, #047857)',
      color: 'white',
      border: 'none',
      padding: '16px 32px',
      borderRadius: 'clamp(10px, 2vw, 12px)',
      fontSize: 'clamp(14px, 3vw, 16px)',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '20px',
      alignSelf: 'center',
      boxShadow: '0 4px 15px rgba(5, 150, 105, 0.3)',
      minHeight: '48px',
      minWidth: '160px',
      maxWidth: '300px',
      width: '100%',
    },

    // Stats container - responsive grid
    statsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
      gap: '20px',
      marginBottom: '30px',
      width: '100%',
      boxSizing: 'border-box',
    },

    statCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.2s ease',
      border: '1px solid #e5e7eb',
      minHeight: '100px',
      boxSizing: 'border-box',
    },

    statCardTotal: {
      borderLeft: '4px solid #3b82f6',
    },

    statCardParticipants: {
      borderLeft: '4px solid #8b5cf6',
    },

    statCardActive: {
      borderLeft: '4px solid #10b981',
    },

    statCardCompleted: {
      borderLeft: '4px solid #f59e0b',
    },

    statIcon: {
      fontSize: '2rem',
      color: '#6b7280',
      flexShrink: 0,
    },

    statContent: {
      flex: 1,
      minWidth: 0,
    },

    statNumber: {
      fontSize: '1.875rem',
      fontWeight: '700',
      lineHeight: '1',
      marginBottom: '4px',
      color: '#1f2937',
    },

    statLabel: {
      fontSize: '0.875rem',
      color: '#6b7280',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
    },

    // Pagination info - responsive
    paginationInfo: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '20px',
      padding: '0 10px',
      flexWrap: 'wrap',
      gap: '10px',
      fontSize: 'clamp(12px, 3vw, 14px)',
    },

    paginationText: {
      color: 'var(--mobile-text-highlight )',
      fontSize: 'clamp(12px, 3vw, 14px)',
    },

    highlight: {
      color: 'var(--mobile-text-highlight)',
      fontWeight: '600',
    },

    // IMPROVED: Training cards container with consistent sizing
    trainingCardsContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '20px',
      marginBottom: '40px',
      width: '100%',
      boxSizing: 'border-box',
      justifyItems: 'stretch',
      alignItems: 'stretch',
    },

    trainingCardWrapper: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      width: '100%',
      height: '100%', // Make wrapper take full height
    },

    // Professional training card design
    trainingCard: {
      background: 'white',
      borderRadius: '12px',
      padding: '20px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      boxShadow: '0 8px 32px rgba(34, 197, 94, 0.12), 0 4px 16px rgba(34, 197, 94, 0.08)',
      border: '1px solid #e5e7eb',
      width: '100%',
      minHeight: '280px',
      maxHeight: 'none',
      boxSizing: 'border-box',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      position: 'relative',
    },

    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px',
      flexWrap: 'wrap',
      gap: '12px',
      flex: 'none', // Don't grow/shrink
    },

    trainerInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      flex: '1',
      minWidth: '180px',
    },

    trainerAvatar: {
      width: '44px',
      height: '44px',
      borderRadius: '50%',
      background: '#3b82f6',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: '600',
      fontSize: '1.125rem',
      flexShrink: 0,
    },

    trainerDetails: {
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
    },

    trainerName: {
      fontSize: '1rem',
      fontWeight: '600',
      color: '#1f2937',
      margin: '0 0 4px 0',
      lineHeight: '1.2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    coTrainerName: {
      fontSize: '0.8rem',
      fontWeight: '500',
      color: '#6b7280',
      margin: '0 0 4px 0',
      lineHeight: '1.2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    trainerLocation: {
      fontSize: '0.875rem',
      color: '#6b7280',
      margin: 0,
      lineHeight: '1.3',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    cardActions: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      flexShrink: 0,
    },

    statusBadge: {
      padding: '4px 10px',
      borderRadius: '12px',
      fontSize: '11px',
      fontWeight: '500',
      textTransform: 'uppercase',
      letterSpacing: '0.025em',
      whiteSpace: 'nowrap',
      lineHeight: '1',
    },

    statusBadgeActive: {
      background: '#dcfdf7',
      color: '#065f46',
      border: '1px solid #a7f3d0',
    },

    statusBadgePending: {
      background: '#fef3c7',
      color: '#92400e',
      border: '1px solid #fcd34d',
    },

    statusBadgeCompleted: {
      background: '#ede9fe',
      color: '#5b21b6',
      border: '1px solid #c4b5fd',
    },

    editBtn: {
      width: '32px',
      height: '32px',
      border: '1px solid #d1d5db',
      borderRadius: '6px',
      background: 'white',
      color: '#6b7280',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      transition: 'all 0.2s ease',
    },

    // IMPROVED: Card details with consistent spacing
    cardDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      width: '100%',
      flex: '1', // Take remaining space
      minHeight: '150px', // Ensure minimum content height
    },

    detailItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
      padding: '10px 12px',
      background: '#f8fafc',
      borderRadius: '8px',
      borderLeft: '3px solid #4f46e5',
      width: '100%',
      boxSizing: 'border-box',
      minHeight: '40px', // Consistent height for all items
    },

    detailIcon: {
      fontSize: '1rem',
      minWidth: '18px',
      flexShrink: 0,
      color: '#4f46e5',
    },

    detailLabel: {
      fontWeight: '600',
      color: '#374151',
      minWidth: '72px',
      fontSize: 'clamp(11px, 3vw, 13px)',
      flexShrink: 0,
    },

    detailValue: {
      color: '#6b7280',
      fontWeight: '500',
      fontSize: 'clamp(11px, 3vw, 13px)',
      flex: 1,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    // IMPROVED: Link section with consistent sizing
    linkSection: {
      background: 'white',
      borderRadius: '10px',
      padding: '14px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      border: '2px solid',
      transition: 'all 0.2s ease',
      flexWrap: 'wrap',
      gap: '12px',
      width: '100%',
      boxSizing: 'border-box',
      minHeight: '70px', // Consistent minimum height
      marginTop: 'auto', // Push to bottom of wrapper
    },

    linkSectionAccessible: {
      borderColor: '#10b981',
      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(5, 150, 105, 0.05))',
    },

    linkSectionExpired: {
      borderColor: '#ef4444',
      background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05), rgba(220, 38, 38, 0.05))',
    },

    linkInfo: {
      flex: 1,
      minWidth: '120px',
    },

    linkStatus: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontWeight: '600',
      marginBottom: '4px',
      fontSize: 'clamp(11px, 3vw, 13px)',
    },

    linkStatusAccessible: {
      color: '#f7f7f7ff',
    },

    linkStatusExpired: {
      color: '#0018cfff',
    },

    linkIcon: {
      fontSize: '10px',
    },

    linkText: {
      color: '#374151',
      fontSize: 'clamp(11px, 3vw, 13px)',
    },

    linkExpire: {
      fontSize: 'clamp(10px, 3vw, 11px)',
      color: '#000000ff',
      fontWeight: '500',
    },

    // Enhanced expire info styles
    expireInfoContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
    },

    expireStatusBadge: (expireInfo) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '4px',
      padding: '2px 6px',
      borderRadius: '4px',
      backgroundColor: expireInfo.bgColor,
      border: `1px solid ${expireInfo.statusColor}20`,
      fontSize: 'clamp(9px, 2.5vw, 10px)',
      fontWeight: '600',
      color: expireInfo.statusColor,
      alignSelf: 'flex-start',
    }),

    expireIcon: {
      fontSize: 'clamp(8px, 2vw, 10px)',
    },

    expireStatusText: {
      fontSize: 'clamp(9px, 2.5vw, 10px)',
      fontWeight: '600',
    },

    expireDetails: {
      display: 'flex',
      flexDirection: 'column',
      gap: '2px',
    },

    expireMessage: {
      fontSize: 'clamp(9px, 2.5vw, 10px)',
      color: '#6b7280',
      fontWeight: '500',
    },

    expireDate: {
      fontSize: 'clamp(8px, 2vw, 9px)',
      color: '#9ca3af',
      fontWeight: '400',
    },

    linkActions: {
      display: 'flex',
      gap: '6px',
      alignItems: 'center',
      flexWrap: 'wrap',
    },

    linkBtn: {
      padding: '6px 10px',
      border: 'none',
      borderRadius: '6px',
      fontSize: 'clamp(10px, 3vw, 11px)',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '3px',
      minHeight: '28px',
      whiteSpace: 'nowrap',
    },

    linkBtnCopy: {
      background: '#6b7280',
      color: 'white',
    },

    linkBtnCopied: {
      background: '#10b981',
      color: 'white',
    },

    linkBtnOpen: {
      background: '#059669',
      color: 'white',
    },

    linkBtnExpired: {
      background: '#dc2626',
      color: 'white',
      cursor: 'not-allowed',
    },

    // Pagination - responsive
    paginationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '10px',
      marginTop: '40px',
      flexWrap: 'wrap',
      padding: '0 10px',
      boxShadow: '0 4px 20px rgba(236, 72, 153, 0.1), 0 2px 8px rgba(236, 72, 153, 0.05)',
    },

    paginationBtn: {
      padding: '10px 16px',
      border: '2px solid rgba(255,255,255,0.3)',
      borderRadius: '10px',
      background: 'rgba(255,255,255,0.1)',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: 'clamp(12px, 3vw, 14px)',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      backdropFilter: 'blur(10px)',
      minHeight: '40px',
      whiteSpace: 'nowrap',
    },

    paginationBtnDisabled: {
      opacity: '0.5',
      cursor: 'not-allowed',
    },

    pageNumbers: {
      display: 'flex',
      gap: '5px',
      flexWrap: 'wrap',
      justifyContent: 'center',
    },

    pageNumber: {
      width: '40px',
      height: '40px',
      border: '2px solid rgba(255,255,255,0.3)',
      borderRadius: '10px',
      background: 'rgba(255,255,255,0.1)',
      color: '#ffffff',
      cursor: 'pointer',
      fontSize: 'clamp(12px, 3vw, 14px)',
      fontWeight: '600',
      transition: 'all 0.2s ease',
      backdropFilter: 'blur(10px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },

    pageNumberActive: {
      background: 'white',
      color: '#4f46e5',
      borderColor: 'white',
    },

    // Loading skeleton with consistent height
    skeleton: {
      background: 'linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)',
      backgroundSize: '200% 100%',
      animation: 'loading 1.5s infinite',
      minHeight: '320px', // Match card minimum height
      borderRadius: 'clamp(12px, 3vw, 16px)',
    },

    skeletonLine: {
      height: '18px',
      borderRadius: '4px',
      marginBottom: '8px',
    },

    skeletonLineTitle: {
      height: '20px',
      width: '70%',
    },

    skeletonLineText: {
      width: '100%',
    },

    // Empty state
    emptyState: {
      gridColumn: '1 / -1',
      textAlign: 'center',
      padding: '60px 20px',
      color: '#f0f0f0',
    },

    emptyIcon: {
      fontSize: 'clamp(3rem, 8vw, 4rem)',
      marginBottom: '20px',
    },

    emptyStateH3: {
      fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
      marginBottom: '10px',
      color: '#ffffff',
    },

    emptyStateP: {
      fontSize: 'clamp(0.9rem, 3vw, 1rem)',
      opacity: '0.8',
      color: '#f0f0f0',
    },

    // Modal styles (keeping your existing modal styles)
    modalOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.8)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      boxSizing: 'border-box',
    },

    modalContainer: {
      background: 'white',
      borderRadius: 'clamp(15px, 3vw, 20px)',
      maxWidth: 'min(800px, 95vw)',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 35px 70px rgba(99, 102, 241, 0.25), 0 20px 40px rgba(99, 102, 241, 0.15)',
      boxSizing: 'border-box',
    },

    modalHeader: {
      padding: '25px 25px 0 25px',
      borderBottom: '1px solid #e5e7eb',
      marginBottom: '25px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      flexWrap: 'wrap',
      gap: '15px',
    },

    modalHeaderContent: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
      flex: 1,
      minWidth: 0,
    },

    modalTrainerAvatar: {
      width: '60px',
      height: '60px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: '700',
      fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
      flexShrink: 0,
    },

    modalTrainerInfo: {
      flex: 1,
      minWidth: 0,
    },

    modalTitle: {
      fontSize: 'clamp(1.2rem, 4vw, 1.5rem)',
      fontWeight: '700',
      color: '#1f2937',
      margin: '0 0 10px 0',
      lineHeight: '1.2',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    modalStatus: {
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: 'clamp(12px, 3vw, 14px)',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      whiteSpace: 'nowrap',
    },

    modalStatusActive: {
      background: 'linear-gradient(135deg, #10b981, #059669)',
      color: 'white',
    },

    modalStatusPending: {
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      color: 'white',
    },

    modalStatusCompleted: {
      background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
      color: 'white',
    },

    modalClose: {
      width: '40px',
      height: '40px',
      border: 'none',
      borderRadius: '50%',
      background: '#f3f4f6',
      color: '#6b7280',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '18px',
      transition: 'all 0.2s ease',
      flexShrink: 0,
    },

    modalContent: {
      padding: '0 25px 25px 25px',
    },

    modalSection: {
      marginBottom: '30px',
    },

    sectionTitle: {
      fontSize: 'clamp(1.1rem, 3vw, 1.2rem)',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '20px',
      paddingBottom: '10px',
      borderBottom: '2px solid #e5e7eb',
    },

    detailGrid: {
      display: 'grid',
      gap: '15px',
      width: '100%',
    },

    detailRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '15px',
      background: '#f8fafc',
      borderRadius: '10px',
      borderLeft: '4px solid #4f46e5',
      gap: '10px',
      flexWrap: 'wrap',
      boxSizing: 'border-box',
    },

    detailRowFull: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      gap: '10px',
    },

    detailKey: {
      fontWeight: '600',
      color: '#374151',
      minWidth: '120px',
      fontSize: 'clamp(12px, 3vw, 14px)',
      flexShrink: 0,
    },

    detailVal: {
      color: '#6b7280',
      textAlign: 'right',
      flex: 1,
      fontSize: 'clamp(12px, 3vw, 14px)',
      wordBreak: 'break-word',
    },

    // Modal expire info styles
    modalExpireInfo: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-end',
      gap: '6px',
      flex: 1,
    },

    modalExpireBadge: (expireInfo) => ({
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      padding: '4px 10px',
      borderRadius: '6px',
      backgroundColor: expireInfo.bgColor,
      border: `1px solid ${expireInfo.statusColor}30`,
      fontSize: 'clamp(10px, 2.5vw, 12px)',
      fontWeight: '600',
      color: expireInfo.statusColor,
      alignSelf: 'flex-end',
    }),

    modalExpireIcon: {
      fontSize: 'clamp(10px, 2.5vw, 12px)',
    },

    modalExpireStatus: {
      fontSize: 'clamp(10px, 2.5vw, 12px)',
      fontWeight: '600',
    },

    modalExpireMessage: {
      fontSize: 'clamp(10px, 2vw, 11px)',
      color: '#9ca3af',
      fontWeight: '400',
      fontStyle: 'italic',
      textAlign: 'right',
      marginTop: '2px',
    },

    productsList: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginTop: '8px',
      width: '100%',
    },

    productTag: {
      background: 'linear-gradient(135deg, #4f46e5, #7c3aed)',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: 'clamp(10px, 3vw, 12px)',
      fontWeight: '500',
      whiteSpace: 'nowrap',
    },

    // Participants section - responsive
    participantsGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
      gap: '15px',
      width: '100%',
    },

    participantCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '15px',
      padding: '15px',
      background: '#f8fafc',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      transition: 'all 0.2s ease',
      width: '100%',
      boxSizing: 'border-box',
    },

    participantAvatar: {
      width: '40px',
      height: '40px',
      borderRadius: '50%',
      background: 'linear-gradient(135deg, #f59e0b, #d97706)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: 'white',
      fontWeight: '600',
      fontSize: '1rem',
      flexShrink: 0,
    },

    participantInfo: {
      flex: 1,
      minWidth: 0,
    },

    participantName: {
      fontWeight: '600',
      color: '#1f2937',
      marginBottom: '4px',
      fontSize: 'clamp(13px, 3vw, 14px)',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    participantContact: {
      fontSize: 'clamp(11px, 3vw, 12px)',
      color: '#6b7280',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },

    participantStatus: {
      width: '30px',
      height: '30px',
      borderRadius: '50%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '14px',
      fontWeight: '600',
      flexShrink: 0,
    },

    participantStatusConfirmed: {
      background: '#10b981',
      color: 'white',
    },

    participantStatusPending: {
      background: '#f59e0b',
      color: 'white',
    },

    // Image preview styles
    imagePreviewContainer: {
      marginTop: '12px',
      padding: '12px',
      border: '1px solid #e5e7eb',
      borderRadius: '8px',
      background: '#f9fafb',
      textAlign: 'center',
    },

    imagePreview: {
      maxWidth: '200px',
      maxHeight: '150px',
      width: 'auto',
      height: 'auto',
      borderRadius: '6px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
      marginBottom: '8px',
      display: 'block',
      margin: '0 auto 8px auto',
    },

    removeImageBtn: {
      background: '#ef4444',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      padding: '6px 12px',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
  };

  // Add responsive CSS for additional media queries
  const additionalCSS = `
  <style>
    @keyframes loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    /* Improved responsive breakpoints for cards */
    @media (max-width: 1400px) {
      .training-cards-container {
        grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)) !important;
      }
    }

    @media (max-width: 1024px) {
      .training-cards-container {
        grid-template-columns: repeat(auto-fill, minmax(300px, 1fr)) !important;
        gap: 18px !important;
      }
    }

    @media (max-width: 768px) {
      .training-cards-container {
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)) !important;
        gap: 16px !important;
      }
      
      .mobile-stack {
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 12px !important;
      }
      
      .mobile-full-width {
        width: 100% !important;
        max-width: none !important;
      }
      
      .mobile-text-center {
        text-align: center !important;
      }
      
      .mobile-hide-text {
        font-size: 0 !important;
      }
      
      .mobile-hide-text::before {
        font-size: 14px !important;
        content: attr(data-mobile-text) !important;
      }
    }

    @media (max-width: 640px) {
      .training-cards-container {
        grid-template-columns: 1fr !important;
        gap: 15px !important;
      }
    }

    @media (max-width: 480px) {
      .training-cards-container {
        grid-template-columns: 1fr !important;
        gap: 12px !important;
      }
      
      .extra-small-text {
        font-size: 12px !important;
      }
      
      .extra-small-padding {
        padding: 8px !important;
      }
    }

    /* Ensure cards maintain consistent aspect ratio */
    .training-card-wrapper {
      aspect-ratio: auto !important;
    }

    .training-card {
      height: auto !important;
      min-height: 320px !important;
    }

    @media (max-width: 480px) {
      .training-card {
        min-height: 280px !important;
      }
    }
  </style>
`;

  // Add CSS keyframes for skeleton animation
  const styleSheet = document.createElement("style");
  styleSheet.innerText = `
    @keyframes loading {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }

    @media (max-width: 768px) {
      .mobile-stack {
        flex-direction: column !important;
        align-items: stretch !important;
      }
      
      .mobile-full-width {
        width: 100% !important;
        max-width: none !important;
      }
      
      .mobile-text-center {
        text-align: center !important;
      }
      
      .mobile-hide-text {
        font-size: 0 !important;
      }
      
      .mobile-hide-text::before {
        font-size: 14px !important;
        content: attr(data-mobile-text) !important;
      }
    }

    @media (max-width: 480px) {
      .extra-small-text {
        font-size: 12px !important;
      }
      
      .extra-small-padding {
        padding: 10px !important;
      }
    }
  `;

  if (!document.head.querySelector('#responsive-training-styles')) {
    styleSheet.id = 'responsive-training-styles';
    document.head.appendChild(styleSheet);
  }

  return (
    <div style={styles.dashPremiumRoot}>
      {/* Enhanced Header Section with Organized Layout */}
      <div style={styles.dashPremiumHeader}>
        {/* Title Section */}
        <div style={styles.headerTitleSection}>
          <h1 style={styles.dashPremiumPageTitle}>🎓 Training Dashboard</h1>
          <p style={styles.dashPremiumPageSubtitle}>Monitor and manage all training sessions with ease</p>
        </div>

        {/* Enhanced Combined Controls Section */}
        <div style={styles.controlsSection}>
          {/* Enhanced Search Section */}
          <div style={styles.searchSection}>
            <div style={styles.searchFiltersRow}>
              <div style={styles.searchWrapper}>
                <div style={styles.searchIcon}>🔍</div>
                <input
                  type="text"
                  placeholder="Search trainings, trainers, locations, venues, products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={styles.searchInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 4px rgba(79, 70, 229, 0.15)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                />
                {searchTerm && (
                  <button
                    style={styles.clearSearchBtn}
                    onClick={() => setSearchTerm('')}
                    onMouseOver={(e) => {
                      e.target.style.background = 'rgba(107, 114, 128, 0.1)';
                      e.target.style.color = '#374151';
                    }}
                    onMouseOut={(e) => {
                      e.target.style.background = 'transparent';
                      e.target.style.color = '#6b7280';
                    }}
                    title="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>

              <div style={styles.filterWrapper}>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  style={styles.filterSelect}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 4px rgba(79, 70, 229, 0.15)';
                    e.target.style.transform = 'translateY(-1px)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255,255,255,0.3)';
                    e.target.style.boxShadow = '0 4px 15px rgba(0,0,0,0.1)';
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <option value="all">🔄 All Status</option>
                  <option value="active">⚡ Active</option>
                  <option value="pending">⏳ Pending</option>
                  <option value="completed">✅ Completed</option>
                </select>
              </div>
            </div>

            {/* Enhanced Buttons Section */}
            <div style={styles.buttonsSection}>
              <button
                style={{
                  ...styles.actionBtn,
                  ...(showCreateForm ? styles.actionBtnDanger : styles.actionBtnPrimary)
                }}
                onClick={toggleCreateForm}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-3px) scale(1.02)';
                  e.target.style.boxShadow = '0 12px 35px rgba(0,0,0,0.35)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.25)';
                }}
              >
                {showCreateForm ? (
                  <>
                    <span style={styles.btnIcon}>✕</span>
                    <span className="mobile-hide-text" data-mobile-text="Close">Close Form</span>
                  </>
                ) : (
                  <>
                    <span style={styles.btnIcon}>+</span>
                    <span className="mobile-hide-text" data-mobile-text="Create">Create Training</span>
                  </>
                )}
              </button>

              <button
                style={{ ...styles.actionBtn, ...styles.actionBtnSuccess }}
                onClick={handleNavigateToTrainers}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-3px) scale(1.02)';
                  e.target.style.boxShadow = '0 12px 35px rgba(0,0,0,0.35)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0) scale(1)';
                  e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.25)';
                }}
              >
                <span style={styles.btnIcon}>👨‍🏫</span>
                <span className="mobile-hide-text" data-mobile-text="Trainer">Register Trainer</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Results Info */}
      {(searchTerm || filterStatus !== 'all') && (
        <div style={styles.searchResultsInfo}>
          <div style={styles.searchResultsText}>
            <span>🔍 Search Results:</span>
            <span style={styles.resultCount}>{filteredTrainings.length}</span>
            <span>training{filteredTrainings.length !== 1 ? 's' : ''} found</span>
            {searchTerm && <span>for "{searchTerm}"</span>}
            {filterStatus !== 'all' && <span>with status "{filterStatus}"</span>}
          </div>
          {(searchTerm || filterStatus !== 'all') && (
            <button
              style={styles.clearAllBtn}
              onClick={clearSearch}
              onMouseOver={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.3)';
                e.target.style.transform = 'translateY(-1px)';
              }}
              onMouseOut={(e) => {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'translateY(0)';
              }}
            >
              Clear All Filters
            </button>
          )}
        </div>
      )}

      {/* Create/Edit Training Form */}
      {showCreateForm && (
        <div style={styles.createTrainingFormContainer}>
          <div style={styles.formHeader}>
            <h2 style={styles.formTitle}>
              {editingTraining ? "✏️ Edit Training" : "🆕 Create New Training"}
            </h2>
          </div>

          <form onSubmit={handleSubmit} style={styles.trainingForm}>
            <div style={styles.formGrid}>
              {/* Location */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>📍 Location *</label>
                <input
                  name="location"
                  value={form.location}
                  onChange={handleChange}
                  placeholder="Enter training location"
                  required
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Venue */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}> Venue *</label>
                <input
                  name="venue"
                  value={form.venue}
                  onChange={handleChange}
                  placeholder="Enter training venue"
                  required
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Start Date */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>📅 Start Date *</label>
                <input
                  type="text"
                  name="startDate"
                  value={form.startDate}
                  onChange={(e) => handleDateInput(e, 'startDate')}
                  placeholder="DD/MM/YYYY"
                  required
                  maxLength="10"
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';

                    // Validate date on blur
                    if (e.target.value && !validateDate(e.target.value)) {
                      alert('Please enter a valid date in DD/MM/YYYY format');
                      setForm(prev => ({ ...prev, startDate: '' }));
                    }
                  }}
                />
                {form.startDate && validateDate(form.startDate) && (
                  <div style={styles.dateDisplay}>
                    📅 {formatDate(parseDate(form.startDate)?.toISOString())}
                  </div>
                )}
              </div>

              {/* End Date */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>📅 End Date *</label>
                <input
                  type="text"
                  name="endDate"
                  value={form.endDate}
                  onChange={(e) => handleDateInput(e, 'endDate')}
                  placeholder="DD/MM/YYYY"
                  required
                  maxLength="10"
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';

                    // Validate date on blur
                    if (e.target.value && !validateDate(e.target.value)) {
                      alert('Please enter a valid date in DD/MM/YYYY format');
                      setForm(prev => ({ ...prev, endDate: '' }));
                    } else if (e.target.value && form.startDate) {
                      const startDate = parseDate(form.startDate);
                      const endDate = parseDate(e.target.value);
                      if (startDate && endDate && endDate < startDate) {
                        alert('End date cannot be before start date');
                        setForm(prev => ({ ...prev, endDate: '' }));
                      }
                    }
                  }}
                />
                {form.endDate && validateDate(form.endDate) && (
                  <div style={styles.dateDisplay}>
                    📅 {formatDate(parseDate(form.endDate)?.toISOString())}
                  </div>
                )}
              </div>

              {/* Start Time */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>🕐 Start Time *</label>
                <input
                  type="time"
                  name="time"
                  value={form.time}
                  onChange={handleChange}
                  required
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Products */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>📦 Products *</label>
                <input
                  name="products"
                  value={form.products}
                  onChange={handleChange}
                  placeholder="Products (comma-separated)"
                  required
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Candidates */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>👥 Candidates *</label>
                <input
                  type="number"
                  name="candidates"
                  value={form.candidates}
                  onChange={handleChange}
                  placeholder="Number of candidates"
                  required
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Trainer */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>👨‍🏫 Trainer *</label>
                <select
                  name="trainerId"
                  value={form.trainerId}
                  onChange={handleChange}
                  required
                  style={styles.formSelect}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  <option value="">Select Trainer</option>
                  {trainers
                    .filter(trainer => {
                      // Check if trainer is active (boolean field)
                      // Also check if not suspended
                      const isActive = trainer.active === true;
                      const isSuspended = trainer.suspendPeriod &&
                        new Date(trainer.suspendPeriod.end) > new Date();
                      return isActive && !isSuspended;
                    })
                    .map((trainer) => (
                      <option key={trainer.id} value={trainer.id}>
                        {trainer.name}
                      </option>
                    ))}
                </select>
                {trainers.filter(t => {
                  const isActive = t.active === true;
                  const isSuspended = t.suspendPeriod &&
                    new Date(t.suspendPeriod.end) > new Date();
                  return isActive && !isSuspended;
                }).length === 0 && (
                    <div style={{
                      marginTop: '8px',
                      padding: '8px 12px',
                      backgroundColor: '#fef3c7',
                      borderRadius: '6px',
                      fontSize: '12px',
                      color: '#92400e',
                      fontWeight: '500',
                      border: '1px solid #fbbf24'
                    }}>
                      ⚠️ No active trainers available. Please activate a trainer first.
                    </div>
                  )}
              </div>

              {/* Co-Trainer */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>👨‍🏫 Co-Trainer (Optional)</label>
                <select
                  name="coTrainerId"
                  value={form.coTrainerId}
                  onChange={handleChange}
                  style={styles.formSelect}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                  }}
                >
                  <option value="">Select Co-Trainer (Optional)</option>
                  {trainers
                    .filter(trainer => {
                      // Check if trainer is active and not the main trainer
                      const isActive = trainer.active === true;
                      const isSuspended = trainer.suspendPeriod &&
                        new Date(trainer.suspendPeriod.end) > new Date();
                      const isNotMainTrainer = trainer.id !== form.trainerId;
                      return isActive && !isSuspended && isNotMainTrainer;
                    })
                    .map((trainer) => (
                      <option key={trainer.id} value={trainer.id}>
                        {trainer.name}
                      </option>
                    ))}
                </select>
              </div>

              {/* Link Expire Date & Time */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>⏰ Link Expire Date & Time *</label>
                <div style={styles.dateTimeContainer}>
                  <div style={styles.dateInputWrapper}>
                    <input
                      type="text"
                      name="expireDate"
                      value={form.expireDate}
                      onChange={(e) => handleDateInput(e, 'expireDate')}
                      placeholder="DD/MM/YYYY"
                      required
                      maxLength="10"
                      style={{ ...styles.formInput, ...styles.dateInput }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4f46e5';
                        e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';

                        // Validate date on blur
                        if (e.target.value && !validateDate(e.target.value)) {
                          alert('Please enter a valid date in DD/MM/YYYY format');
                          setForm(prev => ({ ...prev, expireDate: '' }));
                        } else if (e.target.value && form.startDate) {
                          const startDate = parseDate(form.startDate);
                          const expireDate = parseDate(e.target.value);
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);

                          if (expireDate && expireDate < today) {
                            alert('Expire date cannot be in the past');
                            setForm(prev => ({ ...prev, expireDate: '' }));
                          } else if (startDate && expireDate && expireDate >= startDate) {
                            alert('Link expire date must be before training start date');
                            setForm(prev => ({ ...prev, expireDate: '' }));
                          }
                        }
                      }}
                    />
                  </div>
                  {form.expireDate && validateDate(form.expireDate) && (
                    <div style={styles.expireDateDisplay}>
                      ⏰ {formatDate(parseDate(form.expireDate)?.toISOString())} at {form.expireTime || '23:59'}
                    </div>
                  )}
                  <div style={styles.timeInputWrapper}>
                    <input
                      type="time"
                      name="expireTime"
                      value={form.expireTime || '23:59'}
                      onChange={handleChange}
                      style={{ ...styles.formInput, ...styles.timeInput }}
                      onFocus={(e) => {
                        e.target.style.borderColor = '#4f46e5';
                        e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.target.style.borderColor = '#e5e7eb';
                        e.target.style.boxShadow = 'none';
                      }}
                    />
                  </div>
                </div>
                <div style={styles.expireHelper}>
                  <span style={styles.expireHelperIcon}>💡</span>
                  <span style={styles.expireHelperText}>
                    Set date and time when the link should expire. Default time is 11:59 PM.
                  </span>
                </div>
              </div>

              {/* Duration */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>⏱️ Duration (Hours) *</label>
                <input
                  type="number"
                  name="accessDuration"
                  value={form.accessDuration}
                  onChange={handleChange}
                  placeholder="Duration in hours"
                  required
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Fees */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>💰 Fees (₹) *</label>
                <input
                  type="number"
                  name="fees"
                  value={form.fees}
                  onChange={handleChange}
                  placeholder="Training fees"
                  required
                  min="0"
                  step="0.01"
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>

              {/* Photo Upload */}
              <div style={styles.formGroup}>
                <label style={styles.formLabel}>🖼️ Training Photo (Optional)</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={styles.formInput}
                  onFocus={(e) => {
                    e.target.style.borderColor = '#4f46e5';
                    e.target.style.boxShadow = '0 0 0 3px rgba(79, 70, 229, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = '#e5e7eb';
                    e.target.style.boxShadow = 'none';
                  }}
                />
                {imagePreview && (
                  <div style={styles.imagePreviewContainer}>
                    <img
                      src={imagePreview}
                      alt="Training preview"
                      style={styles.imagePreview}
                    />
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                        setForm(prev => ({ ...prev, photo: '' }));
                      }}
                      style={styles.removeImageBtn}
                    >
                      ✕ Remove
                    </button>
                  </div>
                )}
              </div>
            </div>

            <button
              type="submit"
              style={styles.formSubmitBtn}
              onMouseOver={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 8px 25px rgba(5, 150, 105, 0.4)';
              }}
              onMouseOut={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 15px rgba(5, 150, 105, 0.3)';
              }}
            >
              {editingTraining ? "💾 Update Training" : "✨ Create Training"}
            </button>
          </form>
        </div>
      )}

      {/* Enhanced Stats Cards */}
      <div style={styles.statsContainer}>
        <div
          style={{ ...styles.statCard, ...styles.statCardTotal }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(99, 102, 241, 0.2), 0 10px 25px rgba(99, 102, 241, 0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
          }}
        >
          <div style={styles.statIcon}>📚</div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>{trainings.length}</div>
            <div style={styles.statLabel}>Total Trainings</div>
          </div>
        </div>

        <div
          style={{ ...styles.statCard, ...styles.statCardParticipants }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(99, 102, 241, 0.2), 0 10px 25px rgba(99, 102, 241, 0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
          }}
        >
          <div style={styles.statIcon}>👥</div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>{participantCount}</div>
            <div style={styles.statLabel}>Total Participants</div>
          </div>
        </div>

        <div
          style={{ ...styles.statCard, ...styles.statCardActive }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(99, 102, 241, 0.2), 0 10px 25px rgba(99, 102, 241, 0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
          }}
        >
          <div style={styles.statIcon}>⚡</div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>{activeCount}</div>
            <div style={styles.statLabel}>Active Sessions</div>
          </div>
        </div>

        <div
          style={{ ...styles.statCard, ...styles.statCardCompleted }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-5px)';
            e.currentTarget.style.boxShadow = '0 20px 40px rgba(99, 102, 241, 0.2), 0 10px 25px rgba(99, 102, 241, 0.15)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
          }}
        >
          <div style={styles.statIcon}>✅</div>
          <div style={styles.statContent}>
            <div style={styles.statNumber}>{completedCount}</div>
            <div style={styles.statLabel}>Completed</div>
          </div>
        </div>
      </div>

      {/* Pagination Info */}
      <div style={styles.paginationInfo}>
        <div style={styles.paginationText}>
          Showing <span style={styles.highlight}>{indexOfFirstTraining + 1}-{Math.min(indexOfLastTraining, totalTrainings)}</span> of <span style={styles.highlight}>{totalTrainings}</span> trainings
        </div>
        <div style={styles.paginationText}>
          Page <span style={styles.highlight}>{currentPage}</span> of <span style={styles.highlight}>{totalPages}</span>
        </div>
      </div>

      {/* Enhanced Training Cards */}
      <div style={styles.trainingCardsContainer}>
        {loading ? (
          Array.from({ length: 6 }).map((_, i) => (
            <div key={i} style={{ ...styles.trainingCard, ...styles.skeleton }}>
              <div style={{ ...styles.skeletonLine, ...styles.skeletonLineTitle }}></div>
              <div style={{ ...styles.skeletonLine, ...styles.skeletonLineText }}></div>
              <div style={{ ...styles.skeletonLine, ...styles.skeletonLineText }}></div>
            </div>
          ))
        ) : currentTrainings.length === 0 ? (
          <div style={styles.emptyState}>
            <div style={styles.emptyIcon}>📋</div>
            <h3 style={styles.emptyStateH3}>No trainings found</h3>
            <p style={styles.emptyStateP}>
              {searchTerm || filterStatus !== 'all'
                ? 'Try adjusting your search or filter criteria'
                : 'Create your first training to get started'}
            </p>
          </div>
        ) : (
          currentTrainings.map((t, idx) => (
            <div key={t.id} style={styles.trainingCardWrapper}>
              <div
                style={styles.trainingCard}
                onClick={() => setSelected(t)}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 25px 50px rgba(34, 197, 94, 0.25), 0 15px 35px rgba(34, 197, 94, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(34, 197, 94, 0.12), 0 4px 16px rgba(34, 197, 94, 0.08)';
                }}
              >
                <div style={styles.cardHeader} className="mobile-stack">
                  <div style={styles.trainerInfo}>
                    <div style={styles.trainerAvatar}>
                      {t.trainerName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div style={styles.trainerDetails}>
                      <h3 style={styles.trainerName}>
                        {t.trainerName || "Unknown Trainer"}
                      </h3>
                      {t.coTrainerName && (
                        <p style={styles.coTrainerName}>
                          👥 Co-Trainer: {t.coTrainerName}
                        </p>
                      )}
                      <p style={styles.trainerLocation}>
                        📍 {t.location || 'No location'}
                      </p>
                    </div>
                  </div>

                  <div style={styles.cardActions} className="mobile-full-width mobile-stack">
                    <span style={{
                      ...styles.statusBadge,
                      ...(isActiveTraining(t)
                        ? styles.statusBadgeActive
                        : t.status === 'completed'
                          ? styles.statusBadgeCompleted
                          : styles.statusBadgePending
                      )
                    }}>
                      {isActiveTraining(t) ? "⚡ Active" : `📌 ${t.status || 'Pending'}`}
                    </span>
                    {isActiveTraining(t) && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleEditTraining(t);
                        }}
                        style={styles.editBtn}
                        title="Update Training"
                        onMouseOver={(e) => {
                          e.target.style.transform = 'scale(1.1)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.transform = 'scale(1)';
                        }}
                      >
                        ✏️
                      </button>
                    )}
                  </div>
                </div>

                <div style={styles.cardDetails}>
                  <div style={styles.detailItem}>
                    <span style={styles.detailIcon}>📅</span>
                    <span style={styles.detailLabel}>Start Date</span>
                    <span style={styles.detailValue}>
                      {formatDate(getStartDate(t), 'start date')}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailIcon}>👥</span>
                    <span style={styles.detailLabel}>Participants</span>
                    <span style={styles.detailValue}>
                      {t.participants ? Object.keys(t.participants).length : 0}
                    </span>
                  </div>
                  <div style={styles.detailItem}>
                    <span style={styles.detailIcon}>.</span>
                    <span style={styles.detailLabel}>Venue</span>
                    <span style={styles.detailValue}>{t.venue || '—'}</span>
                  </div>
                </div>
              </div>

              {/* Enhanced Training Link Section */}
              {t.joinLink && (
                <div style={{
                  ...styles.linkSection,
                  ...(isLinkAccessible(t)
                    ? styles.linkSectionAccessible
                    : styles.linkSectionExpired
                  )
                }}>
                  <div style={styles.linkInfo}>
                    <div style={styles.linkStatus}>
                      <span style={styles.linkIcon}>{isLinkAccessible(t) ? '🟢' : '🔴'}</span>
                      <span style={styles.linkText}>
                        Training Link: {isLinkAccessible(t) ? 'Active' : 'Expired'}
                      </span>
                    </div>
                    <div style={{ ...styles.linkExpire, ...styles.expireInfoContainer }}>
                      <div style={styles.expireStatusBadge(getExpireInfo(t))}>
                        <span style={styles.expireIcon}>{getExpireInfo(t).icon}</span>
                        <span style={styles.expireStatusText}>{getExpireInfo(t).status}</span>
                      </div>
                      <div style={styles.expireDetails}>
                        <span style={styles.expireMessage}>{getExpireInfo(t).message}</span>
                        <span style={styles.expireDate}>{formatDate(t.expireDate)}</span>
                      </div>
                    </div>
                  </div>

                  <div style={styles.linkActions}>
                    {isLinkAccessible(t) ? (
                      <>
                        <button
                          onClick={() => copyTrainingLink(t.joinLink, t.id)}
                          style={{
                            ...styles.linkBtn,
                            ...(copiedId === t.id ? styles.linkBtnCopied : styles.linkBtnCopy)
                          }}
                          title="Copy Training Link"
                          onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          {copiedId === t.id ? '✓ Copied!' : '📋 Copy'}
                        </button>

                        <a
                          href={t.joinLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ ...styles.linkBtn, ...styles.linkBtnOpen }}
                          onMouseOver={(e) => {
                            e.target.style.transform = 'translateY(-1px)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.transform = 'translateY(0)';
                          }}
                        >
                          🔗 Open
                        </a>
                      </>
                    ) : (
                      <span style={{ ...styles.linkBtn, ...styles.linkBtnExpired }}>
                        ❌ Expired
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Enhanced Pagination */}
      {totalPages > 1 && (
        <div style={styles.paginationContainer}>
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            style={{
              ...styles.paginationBtn,
              ...(currentPage === 1 ? styles.paginationBtnDisabled : {})
            }}
            onMouseOver={(e) => {
              if (currentPage !== 1) {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseOut={(e) => {
              if (currentPage !== 1) {
                e.target.style.background = 'rgba(255,255,255,0.1)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            ← Previous
          </button>

          <div style={styles.pageNumbers}>
            {Array.from({ length: Math.min(totalPages, 7) }, (_, i) => {
              let pageNum;
              if (totalPages <= 7) {
                pageNum = i + 1;
              } else if (currentPage <= 4) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 3) {
                pageNum = totalPages - 6 + i;
              } else {
                pageNum = currentPage - 3 + i;
              }

              if (pageNum < 1 || pageNum > totalPages) return null;

              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  style={{
                    ...styles.pageNumber,
                    ...(currentPage === pageNum ? styles.pageNumberActive : {})
                  }}
                  onMouseOver={(e) => {
                    if (currentPage !== pageNum) {
                      e.target.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (currentPage !== pageNum) {
                      e.target.style.background = 'rgba(255,255,255,0.1)';
                    }
                  }}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            style={{
              ...styles.paginationBtn,
              ...(currentPage === totalPages ? styles.paginationBtnDisabled : {})
            }}
            onMouseOver={(e) => {
              if (currentPage !== totalPages) {
                e.target.style.background = 'rgba(255,255,255,0.2)';
                e.target.style.transform = 'translateY(-1px)';
              }
            }}
            onMouseOut={(e) => {
              if (currentPage !== totalPages) {
                e.target.style.background = 'rgba(255,255,255,0.1)';
                e.target.style.transform = 'translateY(0)';
              }
            }}
          >
            Next →
          </button>
        </div>
      )}

      {/* Enhanced Modal */}
      {selected && (
        <div
          style={styles.modalOverlay}
          onClick={() => setSelected(null)}
        >
          <div
            style={styles.modalContainer}
            onClick={e => e.stopPropagation()}
          >
            <div style={styles.modalHeader}>
              <div style={styles.modalHeaderContent}>
                <div style={styles.modalTrainerAvatar}>
                  {selected.trainerName?.charAt(0)?.toUpperCase() || '?'}
                </div>
                <div style={styles.modalTrainerInfo}>
                  <h2 style={styles.modalTitle}>
                    {selected.trainerName || "Training Details"}
                  </h2>
                  <span style={{
                    ...styles.modalStatus,
                    ...(isActiveTraining(selected)
                      ? styles.modalStatusActive
                      : selected.status === 'completed'
                        ? styles.modalStatusCompleted
                        : styles.modalStatusPending
                    )
                  }}>
                    {isActiveTraining(selected) ? "⚡ Active" : `📌 ${selected.status || 'Pending'}`}
                  </span>
                </div>
              </div>
              <button
                style={styles.modalClose}
                onClick={() => setSelected(null)}
                aria-label="Close modal"
                onMouseOver={(e) => {
                  e.target.style.background = '#e5e7eb';
                  e.target.style.color = '#374151';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = '#f3f4f6';
                  e.target.style.color = '#6b7280';
                }}
              >
                ✕
              </button>
            </div>

            <div style={styles.modalContent}>
              <div style={styles.modalSection}>
                <h3 style={styles.sectionTitle}>📋 Training Information</h3>
                <div style={styles.detailGrid}>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>🆔 Trainer ID</span>
                    <span style={styles.detailVal}>{selected.trainerId || "—"}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>👨‍🏫 Trainer Name</span>
                    <span style={styles.detailVal}>{selected.trainerName || "—"}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>📞 Trainer Phone</span>
                    <span style={styles.detailVal}>{selected.trainerPhone || "—"}</span>
                  </div>
                  {selected.coTrainerName && (
                    <>
                      <div style={styles.detailRow}>
                        <span style={styles.detailKey}>👥 Co-Trainer Name</span>
                        <span style={styles.detailVal}>{selected.coTrainerName}</span>
                      </div>
                      <div style={styles.detailRow}>
                        <span style={styles.detailKey}>📱 Co-Trainer Phone</span>
                        <span style={styles.detailVal}>{selected.coTrainerPhone || "—"}</span>
                      </div>
                    </>
                  )}
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>📅 Start Date</span>
                    <span style={styles.detailVal}>
                      {formatDate(getStartDate(selected), 'modal start date')}
                    </span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>🕐 Time</span>
                    <span style={styles.detailVal}>{selected.time || "—"}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>⏰ Expire Date</span>
                    <div style={styles.modalExpireInfo}>
                      <div style={styles.modalExpireBadge(getExpireInfo(selected))}>
                        <span style={styles.modalExpireIcon}>{getExpireInfo(selected).icon}</span>
                        <span style={styles.modalExpireStatus}>{getExpireInfo(selected).status}</span>
                      </div>
                      <span style={styles.detailVal}>
                        {formatDate(getExpireDate(selected), 'modal expire date')}
                      </span>
                      <span style={styles.modalExpireMessage}>{getExpireInfo(selected).detailedMessage}</span>
                    </div>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>📍 Location</span>
                    <span style={styles.detailVal}>{selected.location || "—"}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>. Venue</span>
                    <span style={styles.detailVal}>{selected.venue || "—"}</span>
                  </div>
                  <div style={styles.detailRow}>
                    <span style={styles.detailKey}>💰 Training Fees</span>
                    <span style={styles.detailVal}>₹{selected.fees || selected.Fees || "—"}</span>
                  </div>

                  {selected.products && selected.products.length > 0 && (
                    <div style={{ ...styles.detailRow, ...styles.detailRowFull }}>
                      <span style={styles.detailKey}>📦 Products</span>
                      <span style={styles.detailVal}>
                        <div style={styles.productsList}>
                          {selected.products.map((product, idx) => (
                            <span key={idx} style={styles.productTag}>
                              {product}
                            </span>
                          ))}
                        </div>
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Participants Section */}
              {selected.participants && Object.keys(selected.participants).length > 0 && (
                <div style={styles.modalSection}>
                  <h3 style={styles.sectionTitle}>
                    👥 Participants ({Object.keys(selected.participants).length})
                  </h3>
                  <div style={styles.participantsGrid}>
                    {Object.values(selected.participants).map((participant, i) => (
                      <div
                        key={i}
                        style={styles.participantCard}
                        onMouseOver={(e) => {
                          e.currentTarget.style.background = '#f1f5f9';
                          e.currentTarget.style.transform = 'translateY(-1px)';
                        }}
                        onMouseOut={(e) => {
                          e.currentTarget.style.background = '#f8fafc';
                          e.currentTarget.style.transform = 'translateY(0)';
                        }}
                      >
                        <div style={styles.participantAvatar}>
                          {participant.name?.charAt(0)?.toUpperCase() || "?"}
                        </div>
                        <div style={styles.participantInfo}>
                          <div style={styles.participantName}>
                            {participant.name}
                          </div>
                          <div style={styles.participantContact}>
                            {participant.email}
                          </div>
                        </div>
                        <div style={{
                          ...styles.participantStatus,
                          ...(participant.status === 'confirmed' || participant.confirmedByTrainer
                            ? styles.participantStatusConfirmed
                            : styles.participantStatusPending
                          )
                        }}>
                          {participant.status === 'confirmed' || participant.confirmedByTrainer
                            ? "✓" : "⏳"}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
